// src/modules/app-views/landingPages/pages/Document/components/AIPromptPanel.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Flex, Typography, Spin } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import landingPagesService from '../../../service';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AIPromptPanel = ({ documentId, onHtmlGenerated }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-scroll to bottom when new messages arrive (skip initial empty render)
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!currentPrompt.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: currentPrompt.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentPrompt('');
    setLoading(true);

    try {
      const response = await landingPagesService.generateWithAI(documentId, userMessage.content);

      const aiMessage = {
        role: 'assistant',
        content: response?.message || translateX('generation_completed'),
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, aiMessage]);

      // Update preview panel with generated HTML
      if (response?.htmlContent && typeof onHtmlGenerated === 'function') {
        onHtmlGenerated(response.htmlContent);
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: error?.message || translateX('generation_failed'),
        isError: true,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = e => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card
      title={translateX('ai_assistant')}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 16,
        },
      }}
    >
      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          marginBottom: 16,
          minHeight: 200,
        }}
      >
        {messages.length === 0 && (
          <Flex
            vertical
            align="center"
            justify="center"
            style={{ height: '100%', opacity: 0.5 }}
          >
            <RobotOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <Typography.Text type="secondary">
              {translateX('prompt_placeholder')}
            </Typography.Text>
          </Flex>
        )}

        {messages.map((msg, idx) => (
          <Flex
            key={idx}
            gap={8}
            style={{
              marginBottom: 12,
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            {msg.role === 'assistant' && (
              <RobotOutlined style={{ fontSize: 16, marginTop: 4 }} />
            )}
            <div
              style={{
                maxWidth: '80%',
                padding: '8px 12px',
                borderRadius: 8,
                backgroundColor: msg.role === 'user' ? '#1677ff' : msg.isError ? '#fff2f0' : '#f5f5f5',
                color: msg.role === 'user' ? '#fff' : msg.isError ? '#ff4d4f' : 'inherit',
              }}
            >
              <Typography.Text
                style={{
                  color: 'inherit',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {msg.content}
              </Typography.Text>
            </div>
            {msg.role === 'user' && (
              <UserOutlined style={{ fontSize: 16, marginTop: 4 }} />
            )}
          </Flex>
        ))}

        {loading && (
          <Flex gap={8} style={{ marginBottom: 12 }}>
            <RobotOutlined style={{ fontSize: 16, marginTop: 4 }} />
            <Spin size="small" />
          </Flex>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <Flex gap={8}>
        <Input.TextArea
          value={currentPrompt}
          onChange={e => setCurrentPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={translateX('prompt_placeholder')}
          autoSize={{ minRows: 2, maxRows: 4 }}
          disabled={loading}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleGenerate}
          loading={loading}
          disabled={!currentPrompt.trim()}
          style={{ alignSelf: 'flex-end' }}
        >
          {translateX('generate')}
        </Button>
      </Flex>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AIPromptPanel;
